/**
 * Documentos que o escritório publica para a empresa (E14): o feed de posts, as pastas por seção
 * e período, a lista filtrada e a URL assinada do arquivo. Desde E16, as guias: a lista filtrada
 * por situação (`guide_status`), o "Paguei" e o desfazer.
 *
 * Toda rota exige a empresa (`?client=`): sem ela o backend responde 400 `CLIENT_REQUIRED`, e uma
 * empresa fora do alcance do usuário dá 404 `COMPANY_NOT_FOUND`. O arquivo se baixa pela URL
 * assinada com `fetchDocumentBlob` (`~/api/files`), o mesmo caminho do PDF das notas.
 */
import { AxiosError } from 'axios';

import { apiClient } from '~/api/client';
import { type CodedError, readCodedError } from '~/api/codedError';
import { fetchDocumentBlob } from '~/api/files';
import type { components, operations } from '~/types/api';

/** `Document` do schema; o nome evita a sombra do `Document` do DOM. */
export type CompanyDocument = components['schemas']['Document'];
export type FeedPost = components['schemas']['FeedPost'];
export type FeedPage = components['schemas']['PaginatedFeedPostList'];
export type DocumentPage = components['schemas']['PaginatedDocumentList'];
/** Uma seção das pastas, com os tipos e os períodos que têm documento atual. */
export type DocumentSection = components['schemas']['Section'];
export type DocumentFileUrl = components['schemas']['DocumentFileUrl'];
/** A guia aninhada no documento (E16): o que o modelo leu, a situação e o pagamento. */
export type TaxGuide = components['schemas']['TaxGuide'];
/** A situação calculada pelo servidor: em aberto, vencida, paga ou substituída. */
export type GuideStatus = components['schemas']['GuideStatusEnum'];
/** Quem marcou a guia como paga: o cliente pelo app, o escritório ou, na E18, a Receita. */
export type PaymentSource = components['schemas']['PaymentSourceEnum'];

type FeedQuery = operations['api_documents_feed_list']['parameters']['query'];
type DocumentListQuery = operations['api_documents_list']['parameters']['query'];
/** `client` e `download`: a empresa em operação e se a URL é assinada para baixar como anexo. */
export type DocumentFileUrlQuery =
  operations['api_documents_file_url_retrieve']['parameters']['query'];
/** Os filtros da lista; a empresa vai à parte, como em toda função deste módulo. */
export type FetchDocumentsParams = Omit<DocumentListQuery, 'client'>;
/** O valor do parâmetro `guide_status`: as guias atuais em aberto, vencidas ou pagas. */
export type GuideStatusFilter = NonNullable<DocumentListQuery['guide_status']>;
type GuidePaymentQuery = operations['api_documents_pay_create']['parameters']['query'];
type GuideUndoQuery = operations['api_documents_unpay_create']['parameters']['query'];

/** Os três segmentos da tela "Impostos", pelo valor que o filtro da lista recebe. */
export const GUIDE_STATUS = {
  open: 'open',
  overdue: 'overdue',
  paid: 'paid',
} as const satisfies Record<string, GuideStatusFilter>;

/** Na ordem dos segmentos: em aberto, vencidas, pagas. */
export const GUIDE_STATUS_FILTERS: readonly GuideStatusFilter[] = Object.values(GUIDE_STATUS);

export function isGuideStatusFilter(value: unknown): value is GuideStatusFilter {
  return GUIDE_STATUS_FILTERS.some((status) => status === value);
}

const PAYABLE_STATUSES: readonly GuideStatus[] = ['OPEN', 'OVERDUE'];
const CLIENT_PAYMENT_SOURCE = 'CLIENT' satisfies PaymentSource;
const PAID_STATUS = 'PAID' satisfies GuideStatus;

/** "Paguei" vale para a guia em aberto e para a vencida; a paga e a substituída não se pagam. */
export function isPayable(guide: TaxGuide): boolean {
  return PAYABLE_STATUSES.includes(guide.status);
}

/**
 * O cliente desfaz só a marcação que veio do app (decisão 4 de E16): a do escritório é a última
 * palavra, e a da Receita é da E18. O servidor recusa o resto com `GUIDE_PAID_BY_OTHER`.
 */
export function isUndoableByClient(guide: TaxGuide): boolean {
  return guide.status === PAID_STATUS && guide.paid_source === CLIENT_PAYMENT_SOURCE;
}

/** Uma página do feed, do mais novo ao mais antigo; `cursor` nulo pede a primeira. */
export async function fetchFeedPage(clientId: number, cursor: string | null): Promise<FeedPage> {
  const params: FeedQuery = cursor === null ? { client: clientId } : { client: clientId, cursor };
  const response = await apiClient.get<FeedPage>('/api/documents/feed/', { params });
  return response.data;
}

export async function fetchSections(clientId: number): Promise<DocumentSection[]> {
  const params: operations['api_documents_sections_retrieve']['parameters']['query'] = {
    client: clientId,
  };
  const response = await apiClient.get<DocumentSection[]>('/api/documents/sections/', {
    params,
  });
  return response.data;
}

export async function fetchDocuments(
  clientId: number,
  filters: FetchDocumentsParams = {},
): Promise<DocumentPage> {
  const params: DocumentListQuery = { ...filters, client: clientId };
  const response = await apiClient.get<DocumentPage>('/api/documents/', { params });
  return response.data;
}

/**
 * Uma página das guias atuais da empresa numa situação, pelo cursor da lista de documentos (o
 * filtro é da lista, não uma rota à parte: decisão 10 de E16). `cursor` nulo pede a primeira.
 */
export function fetchGuides(
  clientId: number,
  status: GuideStatusFilter,
  cursor: string | null = null,
): Promise<DocumentPage> {
  const filters: FetchDocumentsParams =
    cursor === null ? { guide_status: status } : { guide_status: status, cursor };
  return fetchDocuments(clientId, filters);
}

/** O "Paguei": a guia do documento fica paga, agora, pelo app. Devolve o documento atualizado. */
export async function payGuide(id: string, clientId: number): Promise<CompanyDocument> {
  const params: GuidePaymentQuery = { client: clientId };
  const response = await apiClient.post<CompanyDocument>(`/api/documents/${id}/pay/`, null, {
    params,
  });
  return response.data;
}

/** Desfaz o "Paguei": a guia volta a em aberto, ou a vencida pela data. */
export async function unpayGuide(id: string, clientId: number): Promise<CompanyDocument> {
  const params: GuideUndoQuery = { client: clientId };
  const response = await apiClient.post<CompanyDocument>(`/api/documents/${id}/unpay/`, null, {
    params,
  });
  return response.data;
}

export const GUIDE_PAYMENT_NETWORK_FAILURE = 'Falha de conexão. Verifique sua internet.';
export const GUIDE_PAYMENT_FAILURE = 'Não foi possível concluir. Tente de novo.';
/** O código de uma falha que não veio do servidor: a rede ou um corpo sem `{code, message}`. */
const UNCODED_FAILURE = '';

/**
 * A recusa do "Paguei" ou do desfazer na forma que a tela mostra: `{code, message}`. O 409
 * (`GUIDE_ALREADY_PAID`, `GUIDE_SUPERSEDED`, `GUIDE_PAID_BY_OTHER`...) traz a mensagem do servidor,
 * que já diz o que houve.
 */
export class GuidePaymentError extends Error implements CodedError {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'GuidePaymentError';
    this.code = code;
  }
}

export function guidePaymentErrorOf(error: unknown): GuidePaymentError {
  if (!(error instanceof AxiosError)) {
    return new GuidePaymentError(UNCODED_FAILURE, GUIDE_PAYMENT_FAILURE);
  }
  if (!error.response) {
    return new GuidePaymentError(UNCODED_FAILURE, GUIDE_PAYMENT_NETWORK_FAILURE);
  }
  const coded = readCodedError(error.response.data);
  if (!coded) return new GuidePaymentError(UNCODED_FAILURE, GUIDE_PAYMENT_FAILURE);
  return new GuidePaymentError(coded.code, coded.message || GUIDE_PAYMENT_FAILURE);
}

/**
 * URL assinada do arquivo, pedida no momento de abrir ou baixar. O link vale sete dias e não
 * passa por log nenhum; o arquivo vem dele por `fetchDocumentBlob`.
 */
export async function fetchDocumentFileUrl(
  id: string,
  { client, download = false }: DocumentFileUrlQuery,
): Promise<DocumentFileUrl> {
  const params: DocumentFileUrlQuery = download ? { client, download } : { client };
  const response = await apiClient.get<DocumentFileUrl>(`/api/documents/${id}/file-url/`, {
    params,
  });
  return response.data;
}

/** O arquivo baixado e o nome com que ele é salvo no aparelho. */
export interface DocumentFile {
  blob: Blob;
  filename: string;
}

/**
 * O arquivo do documento, para abrir no próprio app: a URL assinada e, dela, os bytes. O CSP só
 * libera o R2 em `connect-src`, então a prévia, o "Abrir" e o "Baixar" usam o blob, nunca a URL.
 */
export async function fetchDocumentFile(id: string, clientId: number): Promise<DocumentFile> {
  const signed = await fetchDocumentFileUrl(id, { client: clientId });
  const blob = await fetchDocumentBlob(signed.url);
  return { blob, filename: signed.filename };
}
